(function (root, factory) {
  const api = factory();
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (root) root.RCQ_RULES = api;
})(typeof window !== "undefined" ? window : globalThis, function () {
  "use strict";
  const typeNames = { dry: "droog", normal: "normaal", combination: "gecombineerd", oily: "vetter" };
  const skinTypes = { dry: "droog", normal: "normaal", combination: "gecombineerd", oily: "vetter" };
  const reactionAdvice = {
    often: "Je vertelt dat je huid geregeld op verzorgingsproducten reageert.",
    sometimes: "Je merkt soms een reactie op verzorgingsproducten.",
    unsure: "Je weet nog niet goed hoe je huid op verzorgingsproducten reageert."
  };
  const drynessNames = { often: "Regelmatig droog of trekkerig", sometimes: "Soms droog of trekkerig", rarely: "Zelden droog of trekkerig", unsure: "Moeilijk in te schatten" };
  const experienceNames = { new: "Nog niet met ESSE gebruikt", some: "Een paar keer met ESSE geprobeerd", regular: "Ik gebruik ESSE al langer" };

  function resolve(a, data = (typeof window !== "undefined" ? window.RCQ_SKIN_TEST : null)) {
    const isNew = a.esseExperience !== "regular";
    const sensitive = a.sensitivity === "often" || a.sensitivity === "sometimes";
    let kit = null;
    let reason = "";

    if (isNew) {
      kit = "sensitive";
      reason = newUserReason(a);
    } else if (sensitive) {
      kit = "sensitive";
      reason = sensitiveReason(a);
    } else if (a.skinType === "unsure" || a.sensitivity === "unsure") {
      reason = uncertainReason(a);
    } else if (a.skinType === "dry") {
      kit = "dry";
      reason = "Je omschrijft je huid als droog en kent ESSE al langer. Daarom adviseren we de Dry Skin Trial Kit, die aansluit bij het huidtype dat je hebt aangegeven.";
    } else if (["normal", "combination", "oily"].includes(a.skinType)) {
      kit = "balanced";
      reason = `Je omschrijft je huid als ${typeNames[a.skinType]} en kent ESSE al langer. Daarom adviseren we de Oily / Combination / Normal Skin Trial Kit, die bij dit huidtype past.`;
    }

    const concerns = Array.isArray(a.concerns) ? a.concerns : [];
    const concernLabels = data ? concerns.map(id => data.concernLabels[id]).filter(Boolean) : [];
    const wantsSimpleRoutine = concerns.includes("simple");
    const adviceParts = [describeSkin(a)];
    if (["often", "sometimes", "unsure"].includes(a.sensitivity) && reactionAdvice[a.sensitivity]) adviceParts.push(reactionAdvice[a.sensitivity]);
    // Maak de combinatie regelmatig droog/trekkerig + eenvoudige routine concreet,
    // maar alleen wanneer beide antwoorden die extra uitleg ondersteunen.
    if (a.dryness === "often" && wantsSimpleRoutine) {
      adviceParts.push("Daarom is het belangrijk om aandacht te besteden aan hydratatie en huidcomfort. Omdat je bovendien graag een eenvoudige routine wilt, adviseren we je om rustig te beginnen en niet meteen allerlei producten te combineren.");
    }
    // Droogte staat al in het antwoord op vraag 4; voorkom dat dezelfde wens direct wordt herhaald.
    const effectiveConcerns = concerns.filter(id => !(id === "dryness" && ["often", "sometimes"].includes(a.dryness)) && !(id === "simple" && a.dryness === "often"));
    adviceParts.push(...effectiveConcerns.map(id => data?.concernAdvice?.[id]).filter(Boolean));
    if (!effectiveConcerns.length && !concerns.length) adviceParts.push("Je koos geen extra huidwens; we houden het advies daarom graag eenvoudig.");
    const advice = adviceParts.length > 1
      ? `${adviceParts[0]}\n\n${adviceParts.slice(1).join(" ")}`
      : adviceParts[0];
    const profile = {
      skinType: a.skinType === "unsure" ? "Ik weet het niet goed" : `Ik omschrijf mijn huid als ${typeNames[a.skinType] || "onbekend"}`,
      dryness: drynessNames[a.dryness] || "Onbekend",
      sensitivity: ({ often: "Mijn huid reageert regelmatig op producten", sometimes: "Mijn huid reageert soms op producten", rarely: "Mijn huid reageert meestal niet opvallend", unsure: "Ik weet het niet zeker" })[a.sensitivity] || "Onbekend",
      experience: experienceNames[a.esseExperience] || "Niet bekend",
      concerns: concernLabels
    };

    return { kit, reason, profile, advice, isNew, sensitive, concerns, unknownType: a.skinType === "unsure" };
  }

  function describeSkin(a) {
    const feeling = {
      often: "Je merkt ook regelmatig droogte of trekkerigheid.",
      sometimes: "Je merkt ook soms droogte of trekkerigheid.",
      rarely: "Een droog of trekkerig gevoel komt bij jou weinig voor.",
      unsure: "Hoe vaak je huid droog of trekkerig voelt, vind je lastig in te schatten."
    }[a.dryness] || "";
    if (a.skinType === "unsure") return `Je vindt het lastig om je huidtype precies te bepalen. ${feeling}`.trim();
    if (a.skinType === "dry") {
      const dryFeeling = { often: "en merkt regelmatig een trekkerig gevoel", sometimes: "en merkt soms een trekkerig gevoel", rarely: "maar merkt zelden droogte of trekkerigheid", unsure: "en vindt het lastig in te schatten hoe vaak ze droog of trekkerig voelt" }[a.dryness];
      return `Je omschrijft je huid als droog ${dryFeeling}.`;
    }
    const type = skinTypes[a.skinType] || "onbekend";
    if (a.skinType === "oily" && ["often", "sometimes"].includes(a.dryness)) return `Je omschrijft je huid als vetter, maar merkt daarnaast ${a.dryness === "often" ? "regelmatig" : "soms"} droogte of trekkerigheid.`;
    return `Je omschrijft je huid als ${type}. ${feeling}`.trim();
  }

  function relatedKitForType(skinType) {
    if (skinType === "dry") return { title: "Dry Skin Trial Kit", name: "de Dry Skin Trial Kit" };
    if (["normal", "combination", "oily"].includes(skinType)) return { title: "Oily / Combination / Normal Skin Trial Kit", name: "de Oily / Combination / Normal Skin Trial Kit" };
    return null;
  }

  function newUserReason(a) {
    const related = relatedKitForType(a.skinType);
    const reaction = a.sensitivity === "often" ? "Je vertelt ook dat je huid geregeld op verzorgingsproducten reageert. " : a.sensitivity === "sometimes" ? "Je vertelt ook dat je huid soms op verzorgingsproducten reageert. " : "";
    if (!related) return `${reaction}Omdat je nog niet of pas een paar keer ESSE hebt gebruikt, adviseren we de Sensitive Skin Trial Set als eerste kennismaking. Je huidtype is nog niet duidelijk genoeg om ook een andere set aan te raden. Als je wilt, kijken we daarna samen verder.`;
    const typeLine = a.skinType === "dry"
      ? `Je antwoorden wijzen op een droge huid. De ${related.title} zou daarom ook bij je huidtype kunnen passen.`
      : `Je omschrijft je huid als ${typeNames[a.skinType]}. De ${related.title} sluit aan bij dat huidtype.`;
    const experience = a.esseExperience === "new" ? "Omdat je ESSE nog niet hebt gebruikt" : "Omdat je ESSE pas een paar keer hebt geprobeerd";
    return `${typeLine} ${reaction}${experience}, adviseren we je eerst rustig kennis te maken met de Sensitive Skin Trial Set. Als je later wilt kijken of ${related.name} past bij wat je huid nodig heeft, denken we graag met je mee.`;
  }

  function sensitiveReason(a) {
    const related = relatedKitForType(a.skinType);
    const reaction = a.sensitivity === "often" ? "Je geeft aan dat je huid regelmatig reageert op verzorgingsproducten." : "Je geeft aan dat je huid soms op verzorgingsproducten reageert.";
    if (related) return `${reaction} Daarom adviseren we de Sensitive Skin Trial Set als voorzichtige keuze. Je huidtype is ook belangrijk: ${related.name} kan later het bekijken waard zijn als reacties minder op de voorgrond staan.`;
    return `${reaction} Daarom adviseren we de Sensitive Skin Trial Set als voorzichtige keuze. Omdat je huidtype nog niet duidelijk is, denken we graag persoonlijk met je mee over een volgende stap.`;
  }

  function uncertainReason(a) {
    if (a.skinType === "dry" && a.sensitivity === "unsure") return "Je omschrijft je huid als droog, waarvoor de Dry Skin Trial Kit in beeld kan komen. Omdat je nog niet goed weet hoe je huid op verzorgingsproducten reageert, willen we niet zomaar een kit kiezen. We helpen je graag persoonlijk verder.";
    if (a.skinType === "unsure" && a.sensitivity === "unsure") return "Je twijfelt nog over je huidtype en weet niet goed hoe je huid op producten reageert. Daarom kiezen we niet op basis van te weinig informatie een Trial Kit. We denken graag persoonlijk met je mee.";
    if (a.skinType === "unsure") return "Je weet nog niet goed welk huidtype bij je past. Daarom adviseren we niet op basis van alleen deze antwoorden een Trial Kit. We denken graag persoonlijk met je mee.";
    const related = relatedKitForType(a.skinType);
    return `Je omschrijft je huid als ${typeNames[a.skinType]}. ${related ? `De ${related.title} zou daarbij kunnen passen. ` : ""}Je weet alleen nog niet goed hoe je huid op verzorgingsproducten reageert. We helpen je liever persoonlijk dan nu een kit te gokken.`;
  }

  return { resolve };
});
